import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Sparkles, X, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { createSparkleEffect } from "@/utils/sounds";
import { MOTIVATIONAL_VIDEOS } from "@/utils/constants";
import axios from "axios";

const API_BASE = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

const MotivateMeButton = () => {
  const [showVideo, setShowVideo] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [watchedVideos, setWatchedVideos] = useState([]);
  const [videos, setVideos] = useState([]);

  // Track analytics events
  const trackEvent = useCallback((eventType, videoId = null, videoTitle = null) => {
    axios.post(`${API_BASE}/api/motivate-me/track`, {
      event_type: eventType,
      video_id: videoId,
      video_title: videoTitle
    }).catch(() => {
      // Silently fail - tracking is non-critical
    });
  }, []);

  // Fetch videos from API on mount
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/motivational-videos`);
        console.log('[MotivateMeButton] Fetched videos from API:', res.data.videos?.length || 0);
        if (res.data.videos && res.data.videos.length > 0) {
          // Convert API format to component format
          const fetchedVideos = res.data.videos.map(v => ({ id: v.video_id, title: v.title }));
          console.log('[MotivateMeButton] Using database videos:', fetchedVideos.map(v => v.title));
          setVideos(fetchedVideos);
        } else {
          // Fallback to hardcoded videos if none in database
          console.log('[MotivateMeButton] No database videos, using hardcoded fallback');
          setVideos(MOTIVATIONAL_VIDEOS);
        }
      } catch (err) {
        console.error('[MotivateMeButton] Failed to fetch, using defaults:', err);
        setVideos(MOTIVATIONAL_VIDEOS);
      }
    };
    fetchVideos();
  }, []);

  const handleMotivateClick = (event) => {
    // Create sparkle effect (same as New Entry button)
    createSparkleEffect(event);
    
    // Track button click
    trackEvent('button_click');
    
    // Use fetched videos or fallback
    const videoList = videos.length > 0 ? videos : MOTIVATIONAL_VIDEOS;
    console.log('[MotivateMeButton] Total videos available:', videoList.length);
    
    // Get available videos (ones not watched yet)
    let availableVideos = videoList.filter(
      video => !watchedVideos.includes(video.id)
    );
    
    // If all videos have been watched, reset the list
    if (availableVideos.length === 0) {
      console.log('[MotivateMeButton] All videos watched, resetting list');
      availableVideos = videoList;
      setWatchedVideos([]);
    }
    
    // Pick a random video from available ones
    const randomIndex = Math.floor(Math.random() * availableVideos.length);
    const selectedVideo = availableVideos[randomIndex];
    console.log('[MotivateMeButton] Selected video:', selectedVideo.title, '| Remaining unwatched:', availableVideos.length - 1);
    
    // Mark this video as watched
    setWatchedVideos(prev => [...prev, selectedVideo.id]);
    
    // Track video play
    trackEvent('video_play', selectedVideo.id, selectedVideo.title);
    
    // Set current video and show modal after sparkle effect
    setCurrentVideo(selectedVideo);
    setTimeout(() => {
      setShowVideo(true);
    }, 400); // Delay to let sparkles show first
  };

  const closeVideo = () => {
    setShowVideo(false);
    setCurrentVideo(null);
  };

  // Video Modal rendered via portal to document.body for true center positioning
  const VideoModal = () => {
    if (!showVideo || !currentVideo) return null;
    
    return createPortal(
      <div 
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md"
        onClick={closeVideo}
        data-testid="video-modal-overlay"
      >
        <div 
          className="relative w-full max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-2xl"
          style={{
            backgroundColor: 'var(--theme-background)',
            boxShadow: '0 25px 50px -12px rgba(var(--theme-primary-rgb), 0.5)',
            border: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.4)'
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div 
            className="flex items-center justify-between p-5"
            style={{ borderBottom: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.2)' }}
          >
            <div className="flex-1" />
            <div className="flex items-center justify-center gap-3">
              <Sparkles className="w-6 h-6 animate-pulse" style={{ color: 'var(--theme-primary)' }} />
              <h3 
                className="text-2xl font-bold"
                style={{ color: 'var(--theme-primary)' }}
              >
                Daily Motivation
              </h3>
              <Sparkles className="w-6 h-6 animate-pulse" style={{ color: 'var(--theme-primary)' }} />
            </div>
            <div className="flex-1 flex justify-end">
              <button
                onClick={closeVideo}
                className="p-2 rounded-full hover:bg-[var(--theme-background-secondary)] transition-colors"
                data-testid="close-video-btn"
              >
                <X className="w-6 h-6" style={{ color: 'var(--theme-primary)' }} />
              </button>
            </div>
          </div>
          
          {/* Video Player - 16:9 aspect ratio */}
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              className="absolute inset-0 w-full h-full"
              src={`https://www.youtube.com/embed/${currentVideo.id}?autoplay=1&rel=0`}
              title="Motivational Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          
          {/* Modal Footer */}
          <div 
            className="p-4 text-center"
            style={{ borderTop: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.2)' }}
          >
            <p className="text-base flex items-center justify-center gap-2" style={{ color: 'var(--theme-text)', opacity: 0.8 }}>
              <Sparkles className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} />
              You've got this! Go crush your day!
              <Sparkles className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} />
            </p>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <>
      <Button
        onClick={handleMotivateClick}
        className="btn-primary w-full flex items-center justify-center gap-2 text-lg py-4 motivate-btn-glow"
        style={{ 
          background: 'var(--theme-button-bg)',
          color: 'white'
        }}
        data-testid="motivate-me-btn"
      >
        <Zap className="w-5 h-5" />
        <span>Motivate Me!</span>
      </Button>

      <VideoModal />
    </>
  );
};

export default MotivateMeButton;
